import { useLogin } from '@/api/login/CreateLogin'
import useTimer from '@/hooks/useTimer'
import useAuthStore from '@/store/useAuthStore'
import faToEnNum from '@/utils/functions/convertNumbers'
import {
  formatPhoneNumber,
  normalizePhoneNumber,
  OTP_COOLDOWN_MS
} from '@/utils/functions/phoneFormatter'
import Drawer from '@dgshahr/ui-kit/Drawer'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { z } from 'zod'
import OtpStep from './OtpStep'
import PhoneNumberStep from './PhoneNumberStep'

interface ILoginModalProps {
  isOpen: boolean
  onClose: () => void
}

interface ISendOtpOptions {
  forceResend?: boolean
}

const phoneNumberSchema = z.string().regex(/^09\d{9}$/, {
  message: 'شماره موبایل باید با 09 شروع شود.'
})

function LoginModal({ isOpen, onClose }: ILoginModalProps) {
  const { phoneNumber, setPhoneNumber, timerStartTimestamp } = useAuthStore()

  const [localPhoneNumber, setLocalPhoneNumber] = useState('')
  const [loadingPhone, setLoadingPhone] = useState(false)
  const [error, setError] = useState<string>('')
  const [step, setStep] = useState<'phone' | 'otp'>('phone')

  const { remainingTime, formattedTime, start } = useTimer({
    initialTime: OTP_COOLDOWN_MS / 1000
  })

  const { mutateAsync: sendLoginOtp } = useLogin({})

  const resetFormState = useCallback(() => {
    setError('')
    setLoadingPhone(false)
  }, [])

  const isOtpCooldownActive = useCallback(
    (rawPhone: string) => {
      if (!timerStartTimestamp || !phoneNumber) return false

      const samePhone =
        normalizePhoneNumber(rawPhone) === normalizePhoneNumber(phoneNumber)
      const withinCooldown =
        Date.now() - timerStartTimestamp < OTP_COOLDOWN_MS

      return samePhone && withinCooldown
    },
    [phoneNumber, timerStartTimestamp]
  )

  useEffect(() => {
    if (!isOpen) return

    resetFormState()

    const hasActiveOtpSession =
      phoneNumber &&
      timerStartTimestamp &&
      Date.now() - timerStartTimestamp < OTP_COOLDOWN_MS

    if (hasActiveOtpSession) {
      setStep('otp')
      setLocalPhoneNumber(formatPhoneNumber(phoneNumber))
    } else {
      setStep('phone')
      setLocalPhoneNumber(phoneNumber ? formatPhoneNumber(phoneNumber) : '')
    }
  }, [isOpen])

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target
    const rawValue = faToEnNum(input.value.replace(/\s/g, ''))
    const cursorPosition = input.selectionStart ?? 0

    const formattedValue = formatPhoneNumber(rawValue)

    let newCursorPosition = cursorPosition
    if (formattedValue.length > localPhoneNumber.length) {
      newCursorPosition += formattedValue.length - localPhoneNumber.length
    }

    setLocalPhoneNumber(formattedValue)
    setError('')

    setTimeout(() => {
      input.setSelectionRange(newCursorPosition, newCursorPosition)
    }, 0)
  }

  const goToOtpStep = () => {
    setStep('otp')
    resetFormState()
  }

  const goBackToPhone = () => {
    setStep('phone')
    setLocalPhoneNumber(
      phoneNumber ? formatPhoneNumber(phoneNumber) : localPhoneNumber
    )
    resetFormState()
  }

  const handleDrawerClose = () => {
    resetFormState()
    onClose()
  }

  const handleSendOtp = async (options?: ISendOtpOptions) => {
    const rawPhone =
      step === 'phone'
        ? normalizePhoneNumber(localPhoneNumber)
        : normalizePhoneNumber(phoneNumber)

    setLoadingPhone(true)
    setError('')

    try {
      phoneNumberSchema.parse(rawPhone)
    } catch (err) {
      setError((err as z.ZodError).errors.at(0)?.message ?? 'شماره نامعتبر است')
      setLoadingPhone(false)
      return
    }

    if (!options?.forceResend && isOtpCooldownActive(rawPhone)) {
      goToOtpStep()
      return
    }

    try {
      await sendLoginOtp({ phone_number: rawPhone })
      setPhoneNumber(formatPhoneNumber(rawPhone))
      start()
      goToOtpStep()
    } catch {
      // toast.error('ارسال کد با خطا مواجه شد')
      setLoadingPhone(false)
    }
  }

  return (
    <Drawer
      header={{
        haveCloseIcon: true,
        title: step === 'phone' ? 'ورود / ثبت نام فروشنده' : 'کد تایید ثبت‌نام'
      }}
      open={isOpen}
      onClose={handleDrawerClose}
      position="center"
      containerClassName="!p-0 !rounded-[28px] !overflow-hidden"
      width={380}
    >
      {step === 'phone' ? (
        <PhoneNumberStep
          phoneNumber={localPhoneNumber}
          error={error}
          loading={loadingPhone}
          handlePhoneNumberChange={handlePhoneNumberChange}
          handleSendOtp={() => handleSendOtp()}
        />
      ) : (
        <OtpStep
          phoneNumber={formatPhoneNumber(phoneNumber)}
          handleSendOtp={handleSendOtp}
          resendLoading={loadingPhone}
          goBack={goBackToPhone}
          remainingTime={remainingTime}
          formattedTime={formattedTime}
          onClose={handleDrawerClose}
        />
      )}
    </Drawer>
  )
}

export default LoginModal
